/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 31st 2022, 12:25:44 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { APIMany2OneSelectFormInput, ConcreteAPIMany2OneSelectFormInputProps, HelperText } from "ui-widgets";
import { APISERVICEPATHS } from "lib/api";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { BillingConfiguration } from "lib/schemas";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { Edit as EditIcon } from "react-feather";
import IconButton from "@mui/material/IconButton";
import { COLORSCHEME } from "theme/styles";
import { resolveURLName } from "core/routes/utils";
import { useServiceBuilderContext } from "service-builder/providers";
import { useNavigate } from "react-router-dom";
import { UUID4 } from "lib/types";

export const BillingConfigurationMany2OnePicker = <TPrimaryObject,>(
  props: ConcreteAPIMany2OneSelectFormInputProps<TPrimaryObject, BillingConfiguration>
) => {
  const { billingConfigurationId, navigateToBillingConfiguration } = useBillingConfigurationMany2OnePicker(
    props.schema_field_key
  );

  return (
    <>
      <APIMany2OneSelectFormInput<TPrimaryObject, BillingConfiguration>
        label={props.label ?? "Billing Configurations"}
        itemIdProperty="id"
        getOptionLabel={(option) => option.name}
        loadingText="Loading available billing configurations"
        apiServicePath={APISERVICEPATHS.billingConfigurations}
        inputStartAdornment={<ReceiptIcon color="primary" />}
        inputEndAdornment={
          <BillingConfigurationEditButton
            billingConfigurationId={billingConfigurationId}
            navigateToBillingConfiguration={navigateToBillingConfiguration}
          />
        }
        {...props}
      />
      <HelperText>
        Create billing configurations to configure fee codes and pricing for products and services for which your
        organization collects money and will require payment during this workflow.
      </HelperText>
    </>
  );
};

const BillingConfigurationEditButton = ({ billingConfigurationId, navigateToBillingConfiguration }) =>
  billingConfigurationId ? (
    <IconButton onClick={navigateToBillingConfiguration}>
      <EditIcon color={COLORSCHEME.primary} />
    </IconButton>
  ) : null;

const useBillingConfigurationMany2OnePicker = <TPrimaryObject,>(
  schema_field_key: ConcreteAPIMany2OneSelectFormInputProps<TPrimaryObject, BillingConfiguration>["schema_field_key"]
) => {
  const { watch } = useOSFormContext<TPrimaryObject>();

  const billingConfigurationId = watch<any>(schema_field_key);

  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const navigate = useNavigate();

  const navigateToBillingConfiguration = () =>
    navigate(
      resolveURLName("billingConfiguration", {
        serviceId: currentProcessDefinition.id,
        billingConfigurationId: billingConfigurationId as UUID4,
      })
    );

  return { billingConfigurationId, navigateToBillingConfiguration };
};
